/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import FiltersDrawer from "@stoneage/App/Widgets/ProductInformation/ProductInformationFiltersDrawer";
import { color } from "@stoneage/XDL/Utilities";
import React, { FC } from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    pagination: state.xProductInfo.data?.pagination,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface ProductInformationControlsContainerStyles {
    container?: InjectableCss;
    filtersButtonGridItem?: InjectableCss;
    sortGridItem?: InjectableCss;
    paginationGridItem?: InjectableCss;
    buttonStyle?: ButtonPresentationProps;
}

export const controlsContainerStyles: ProductInformationControlsContainerStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            padding-top: 12px;
            border-top: 1px solid ${color("common.ui6")};
            @media (max-width: 767px) {
                flex-wrap: wrap;
                justify-content: space-between;
            }
        `,
    },
    filtersButtonGridItem: {
        css: css`
            display: none;
            @media (max-width: 1024px) {
                display: block;
                margin-right: 32px;
            }

            @media (max-width: 481px) {
                width: 100%;
                margin-right: 0;
                margin-bottom: 16px;
            }
        `,
    },
    sortGridItem: {
        css: css`
            margin-right: 22px;
        `,
    },
    paginationGridItem: {
        css: css`
            flex-grow: 1;
        `,
    },
    buttonStyle: {
        icon: { position: "left", src: "Sliders" },
        css: css`
            @media (max-width: 481px) {
                width: 100%;
                justify-content: center;
            }
            & svg {
                transform: rotate(90deg);
            }
        `,
    },
};

const styles = controlsContainerStyles;

const ProductInformationControlsContainer: FC<Props> = ({ id, pagination }) => {
    const dispatch = useDispatch();
    return (
        <>
            <Zone contentId={id} zoneName="titleZone" />
            <StyledWrapper {...styles.container} className="controls-container">
                <StyledWrapper {...styles.filtersButtonGridItem}>
                    <Button
                        {...styles.buttonStyle}
                        onClick={() => {
                            dispatch({
                                type: "Pages/ProductInformation/ToggleFilteresDrawer",
                                isOpen: true,
                            });
                        }}
                    >
                        {translate("Filter Results")}
                    </Button>
                    <FiltersDrawer />
                </StyledWrapper>
                <StyledWrapper {...styles.sortGridItem} className="sort-grid-item">
                    <Zone contentId={id} zoneName="Content00" />
                </StyledWrapper>
                <StyledWrapper {...styles.paginationGridItem} className="pagination-grid-item">
                    <Zone contentId={id} zoneName="Content02" />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductInformationControlsContainer),
    definition: {
        group: "Product Information" as any,
        displayName: "List Controls Container",
        allowedContexts: ["ProductInformationPage"] as any,
    },
};

export default widgetModule;
